/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import pcccLogo from "@/assets/images/pccc-logo.jpg";
import headerBackground from "@/assets/images/header-background.jpg";
import newHeroImage from "@/assets/images/new-hero-image.jpg";

// Payment method icons
import americanExpressSvg from "@/assets/images/payment-methods/americanexpress.svg";
import applePaySvg from "@/assets/images/payment-methods/applepay.svg";
import cashAppSvg from "@/assets/images/payment-methods/cashapp.svg";
import discoverSvg from "@/assets/images/payment-methods/discover.svg";
import googlePaySvg from "@/assets/images/payment-methods/googlepay.svg";
import jcbSvg from "@/assets/images/payment-methods/jcb.svg";
import masterCardSvg from "@/assets/images/payment-methods/mastercard.svg";
import visaSvg from "@/assets/images/payment-methods/visa.svg";

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // Logo mappings
  "/assets/images/pccc-logo.jpg": pcccLogo,
  "/images/pccc-logo.jpg": pcccLogo,
  "/uploads/b/1167372238881f41c2bf1bb1a7b81c68334ea083be4a04f12033eac7494f4ad4/2023%20%20PCCC%20logo_1675266356.jpg": pcccLogo,
  
  // Header background mappings
  "/assets/images/header-background.jpg": headerBackground,
  "/images/header-background.jpg": headerBackground,
  "/uploads/b/1167372238881f41c2bf1bb1a7b81c68334ea083be4a04f12033eac7494f4ad4/20191102_172456_1696295968.jpg": headerBackground,
  
  // New hero image mappings
  "/assets/images/new-hero-image.jpg": newHeroImage,
  "/images/new-hero-image.jpg": newHeroImage,
  "/uploads/b/1167372238881f41c2bf1bb1a7b81c68334ea083be4a04f12033eac7494f4ad4/2023-02-03_15-43-48_1675457041.jpg": newHeroImage,
  
  // Payment method icons
  "/static/icons/payment-methods/americanexpress.svg": americanExpressSvg,
  "/images/payment-methods/americanexpress.svg": americanExpressSvg,
  "/static/icons/payment-methods/applepay.svg": applePaySvg,
  "/images/payment-methods/applepay.svg": applePaySvg,
  "/static/icons/payment-methods/cashapp.svg": cashAppSvg,
  "/images/payment-methods/cashapp.svg": cashAppSvg,
  "/static/icons/payment-methods/discover.svg": discoverSvg,
  "/images/payment-methods/discover.svg": discoverSvg,
  "/static/icons/payment-methods/googlepay.svg": googlePaySvg,
  "/images/payment-methods/googlepay.svg": googlePaySvg,
  "/static/icons/payment-methods/jcb.svg": jcbSvg,
  "/images/payment-methods/jcb.svg": jcbSvg,
  "/static/icons/payment-methods/mastercard.svg": masterCardSvg,
  "/images/payment-methods/mastercard.svg": masterCardSvg,
  "/static/icons/payment-methods/visa.svg": visaSvg,
  "/images/payment-methods/visa.svg": visaSvg,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
